import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Calendar,
  TrendingUp,
  Target,
  Briefcase,
  GraduationCap,
  DollarSign,
  Lightbulb,
  Heart,
  Loader2,
  Download,
  Share2,
  Copy,
  Check,
  ChevronRight,
  Star,
  Rocket,
  Award,
  Brain,
  FileImage,
  FileText,
  Twitter,
  Linkedin,
  User,
  Clock
} from 'lucide-react';
import { GlassCard } from '../components/layout/GlassCard';
import { AIBrainVisualizer } from '../components/ui/AIVoiceVisualizer';
import axios from 'axios';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = '/aira-echo-logo.png';

// Timeline milestone component
const TimelineMilestone = ({ year, data, index, isLast }) => {
  const colors = [
    'from-blue-500 to-cyan-400',
    'from-purple-500 to-pink-400',
    'from-green-500 to-emerald-400',
    'from-orange-500 to-yellow-400',
    'from-red-500 to-rose-400'
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.15 }}
      className="relative flex gap-4"
    >
      {/* Timeline Line */}
      <div className="flex flex-col items-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: index * 0.15 + 0.1 }}
          className={`w-12 h-12 rounded-full bg-gradient-to-r ${colors[index % colors.length]} flex items-center justify-center shadow-lg shadow-primary/30`}
        >
          <span className="text-white font-bold text-sm">Y{year}</span>
        </motion.div>
        {!isLast && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: '100%' }}
            transition={{ delay: index * 0.15 + 0.2, duration: 0.5 }}
            className="w-0.5 bg-gradient-to-b from-primary/50 to-secondary/50 flex-1 my-2"
          />
        )}
      </div>

      {/* Content Card */}
      <motion.div
        whileHover={{ scale: 1.02, x: 10 }}
        className="flex-1 mb-6"
      >
        <GlassCard className="p-5 hover:border-primary/50 transition-all">
          <div className="flex items-center gap-2 mb-3">
            <Rocket className="w-5 h-5 text-accent" />
            <span className="text-xs text-accent font-medium uppercase tracking-wider">
              Year {year}
            </span>
          </div>
          <h4 className="font-heading font-semibold text-lg text-[var(--text-primary)] mb-2">
            {data.role || data.title || `Year ${year} Milestone`}
          </h4>
          <p className="text-[var(--text-secondary)] text-sm mb-3">
            {data.achievements || data.description || 'Significant progress and achievements'}
          </p>
          {data.skills_gained && (
            <div className="flex flex-wrap gap-2">
              {(Array.isArray(data.skills_gained) ? data.skills_gained : [data.skills_gained]).map((skill) => (
                <span key={skill} className="px-2 py-1 text-xs bg-primary/20 text-primary rounded-full">
                  {skill}
                </span>
              ))}
            </div>
          )}
          {data.income_growth && (
            <div className="mt-3 flex items-center gap-2 text-green-400 text-sm">
              <TrendingUp className="w-4 h-4" />
              <span>{data.income_growth}</span>
            </div>
          )}
        </GlassCard>
      </motion.div>
    </motion.div>
  );
};

// Prediction Card Component
const PredictionCard = ({ title, icon: Icon, children, color = 'primary', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
  >
    <GlassCard className="p-6 h-full hover:border-[var(--color-primary)]/50 transition-all">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-r from-${color} to-${color}/50 flex items-center justify-center`}
          style={{ background: color === 'primary' ? 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))' : undefined }}
        >
          <Icon className="w-5 h-5 text-white" />
        </div>
        <h3 className="font-heading font-semibold text-[var(--text-primary)]">{title}</h3>
      </div>
      {children}
    </GlassCard>
  </motion.div>
);

export default function FuturePreviewPage() {
  const { token, user } = useAuth();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [visualizerState, setVisualizerState] = useState('idle');
  const [preview, setPreview] = useState(null);
  const [years, setYears] = useState(5);
  const [activeTab, setActiveTab] = useState('timeline');
  const [copiedLink, setCopiedLink] = useState(false);
  const [downloadingPNG, setDownloadingPNG] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const previewRef = useRef(null);

  // Profile state
  const [profile, setProfile] = useState({
    skills: [],
    interests: [],
    career_goals: '',
    industry: '',
    experience_years: 0
  });
  const [skillInput, setSkillInput] = useState('');
  const [interestInput, setInterestInput] = useState('');

  // Load user profile on mount
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/viral/profile`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        if (response.data) {
          setProfile(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.error('Failed to load profile');
      }
    };
    if (token) loadProfile();
  }, [token]);

  // Add/remove skills and interests
  const addSkill = () => {
    if (skillInput.trim() && !profile.skills.includes(skillInput.trim())) {
      setProfile(prev => ({ ...prev, skills: [...prev.skills, skillInput.trim()] }));
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setProfile(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  };

  const addInterest = () => {
    if (interestInput.trim() && !profile.interests.includes(interestInput.trim())) {
      setProfile(prev => ({ ...prev, interests: [...prev.interests, interestInput.trim()] }));
      setInterestInput('');
    }
  };

  const removeInterest = (interest) => {
    setProfile(prev => ({ ...prev, interests: prev.interests.filter(i => i !== interest) }));
  };

  // Save profile
  const saveProfile = async () => {
    try {
      await axios.patch(`${API_URL}/api/viral/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
    } catch (error) {
      console.error('Failed to save profile');
    }
  };

  // Generate Future Preview
  const generatePreview = async () => {
    setLoading(true);
    setVisualizerState('thinking');

    try {
      await saveProfile();

      const response = await axios.post(
        `${API_URL}/api/viral/future-preview`,
        { years },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );

      setPreview(response.data);
      setVisualizerState('speaking');
      toast.success('Future Life Preview generated!');
      
      // Reset visualizer after 2 seconds
      setTimeout(() => setVisualizerState('idle'), 2000);
    } catch (error) {
      toast.error('Failed to generate preview');
      setVisualizerState('idle');
    } finally {
      setLoading(false);
    }
  };

  // Download as PNG
  const downloadAsPNG = async () => {
    if (!previewRef.current) return;
    setDownloadingPNG(true);

    try {
      const canvas = await html2canvas(previewRef.current, {
        backgroundColor: '#030014',
        scale: 2,
        useCORS: true,
        logging: false
      });

      const link = document.createElement('a');
      link.download = `future_life_preview_${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Downloaded as PNG!');
    } catch (error) {
      toast.error('Failed to download PNG');
    } finally {
      setDownloadingPNG(false);
    }
  };

  // Download as PDF
  const downloadAsPDF = async () => {
    if (!previewRef.current) return;
    setDownloadingPDF(true);

    try {
      const canvas = await html2canvas(previewRef.current, {
        backgroundColor: '#030014',
        scale: 2,
        useCORS: true,
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });

      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(`future_life_preview_${Date.now()}.pdf`);
      toast.success('Downloaded as PDF!');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Copy share link
  const copyShareLink = () => {
    if (preview?.share_url) {
      navigator.clipboard.writeText(`${window.location.origin}${preview.share_url}`);
      setCopiedLink(true);
      toast.success('Link copied!');
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  // Share functions
  const shareOnTwitter = () => {
    const text = `Check out my Future Life Preview from AIRA ECHO! See where I'll be in ${years} years.`;
    const url = `${window.location.origin}${preview?.share_url}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    const url = `${window.location.origin}${preview?.share_url}`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const tabs = [
    { id: 'timeline', label: 'Timeline', icon: Calendar },
    { id: 'career', label: 'Career Path', icon: Briefcase },
    { id: 'financial', label: 'Financial', icon: DollarSign },
    { id: 'skills', label: 'Skills', icon: GraduationCap },
    { id: 'opportunities', label: 'Opportunities', icon: Lightbulb },
    { id: 'growth', label: 'Personal Growth', icon: Heart }
  ];

  return (
    <div className="max-w-7xl mx-auto" data-testid="future-preview-page">
      {/* Header with AI Brain */}
      <div className="text-center mb-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center"
        >
          {/* AI Brain Visualizer */}
          <div className="mb-4">
            <AIBrainVisualizer
              mode="demo"
              size="md"
              state={visualizerState}
              isActive={true}
              showLabel={false}
            />
          </div>
          
          <h1 className="font-heading text-4xl font-bold mb-2">
            <span className="glow-text">Future Life Preview</span>
          </h1>
          <p className="text-[var(--text-secondary)] max-w-2xl">
            Let AIRA's AI brain simulate your future. See your career trajectory, financial growth, 
            skill development, and life milestones for the next {years} years.
          </p>
        </motion.div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Input Section */}
        <div className="lg:col-span-1 space-y-6">
          {/* Profile Form */}
          <GlassCard className="p-6">
            <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2 text-[var(--text-primary)]">
              <User className="w-5 h-5 text-primary" />
              Your Profile
            </h3>

            <div className="space-y-4">
              {/* Years Selector */}
              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-2">Preview Duration</label>
                <div className="flex gap-2">
                  {[3, 5, 10].map((y) => (
                    <button
                      key={y}
                      onClick={() => setYears(y)}
                      className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                        years === y
                          ? 'bg-gradient-to-r from-primary to-secondary text-white'
                          : 'bg-[var(--bg-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)]'
                      }`}
                    >
                      {y} Years
                    </button>
                  ))}
                </div>
              </div>

              {/* Skills */}
              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-2">Your Skills</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    className="input-field flex-1"
                    placeholder="Add a skill..."
                    data-testid="skill-input"
                  />
                  <button onClick={addSkill} className="btn-secondary px-3">+</button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map((skill) => (
                    <span key={skill} className="px-2 py-1 bg-primary/20 text-primary rounded-full text-xs flex items-center gap-1">
                      {skill}
                      <button onClick={() => removeSkill(skill)} className="hover:text-red-400 ml-1">&times;</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Interests */}
              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-2">Interests</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={interestInput}
                    onChange={(e) => setInterestInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addInterest())}
                    className="input-field flex-1"
                    placeholder="Add an interest..."
                    data-testid="interest-input"
                  />
                  <button onClick={addInterest} className="btn-secondary px-3">+</button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.interests.map((interest) => (
                    <span key={interest} className="px-2 py-1 bg-accent/20 text-accent rounded-full text-xs flex items-center gap-1">
                      {interest}
                      <button onClick={() => removeInterest(interest)} className="hover:text-red-400 ml-1">&times;</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Industry */}
              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-2">Industry</label>
                <input
                  type="text"
                  value={profile.industry}
                  onChange={(e) => setProfile(prev => ({ ...prev, industry: e.target.value }))}
                  className="input-field w-full"
                  placeholder="e.g., Technology, Finance..."
                  data-testid="industry-input"
                />
              </div>

              {/* Career Goals */}
              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-2">Career Goals</label>
                <textarea
                  value={profile.career_goals}
                  onChange={(e) => setProfile(prev => ({ ...prev, career_goals: e.target.value }))}
                  className="input-field w-full h-20 resize-none"
                  placeholder="What do you want to achieve?"
                  data-testid="goals-input"
                />
              </div>

              {/* Experience */}
              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-2">Experience (years)</label>
                <input
                  type="number"
                  value={profile.experience_years}
                  onChange={(e) => setProfile(prev => ({ ...prev, experience_years: parseInt(e.target.value) || 0 }))}
                  className="input-field w-full"
                  min="0"
                  data-testid="experience-input"
                />
              </div>

              {/* Generate Button */}
              <button
                onClick={generatePreview}
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2 py-3"
                data-testid="generate-preview-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    AI is simulating your future...
                  </>
                ) : (
                  <>
                    <Brain className="w-5 h-5" />
                    Generate Future Preview
                  </>
                )}
              </button>
            </div>
          </GlassCard>
        </div>

        {/* Preview Section */}
        <div className="lg:col-span-2">
          <AnimatePresence mode="wait">
            {preview ? (
              <motion.div
                key="preview"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                ref={previewRef}
              >
                {/* Preview Header */}
                <GlassCard className="p-6 mb-6 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-primary/20 to-transparent rounded-bl-full" />
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-secondary/20 to-transparent rounded-tr-full" />
                  
                  <div className="relative z-10 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Star className="w-5 h-5 text-accent" />
                        <span className="text-sm text-accent font-medium tracking-wider uppercase">
                          Future Life Preview
                        </span>
                      </div>
                      <h2 className="font-heading text-2xl font-bold text-[var(--text-primary)]">
                        {preview.content?.user_name}'s Journey
                      </h2>
                      <p className="text-[var(--text-secondary)] mt-1">
                        Next {years} years · {preview.content?.current_industry || 'Professional'} · 
                        {preview.content?.success_probability || 75}% Success Probability
                      </p>
                    </div>
                    <div className="flex items-center gap-2 opacity-20">
                      <img src={LOGO_URL} alt="AIRA" className="w-12 h-12" crossOrigin="anonymous" />
                    </div>
                  </div>
                </GlassCard>

                {/* Tabs */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                        activeTab === tab.id
                          ? 'bg-gradient-to-r from-primary to-secondary text-white'
                          : 'bg-[var(--bg-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)]'
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <AnimatePresence mode="wait">
                  {/* Timeline Tab */}
                  {activeTab === 'timeline' && (
                    <motion.div
                      key="timeline"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-4"
                    >
                      <GlassCard className="p-6">
                        <h3 className="font-heading text-lg font-semibold mb-6 flex items-center gap-2 text-[var(--text-primary)]">
                          <Calendar className="w-5 h-5 text-primary" />
                          Your Life Timeline
                        </h3>
                        
                        <div className="space-y-2">
                          {preview.content?.timeline?.map((milestone, idx) => (
                            <TimelineMilestone
                              key={milestone.year || idx}
                              year={milestone.year}
                              data={milestone}
                              index={idx}
                              isLast={idx === preview.content.timeline.length - 1}
                            />
                          ))}
                        </div>

                        {/* Final State */}
                        {preview.content?.final_state && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="mt-6 p-6 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl border border-primary/20"
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <Award className="w-5 h-5 text-accent" />
                              <span className="font-medium text-accent">Your Destination</span>
                            </div>
                            <h4 className="font-heading text-xl font-bold text-[var(--text-primary)] mb-2">
                              {preview.content.final_state.position}
                            </h4>
                            <p className="text-[var(--text-secondary)] mb-4">
                              {preview.content.final_state.lifestyle}
                            </p>
                            {preview.content.final_state.key_accomplishments && (
                              <div className="flex flex-wrap gap-2">
                                {preview.content.final_state.key_accomplishments.map((acc) => (
                                  <span key={acc} className="px-3 py-1 bg-white/10 rounded-full text-sm">
                                    {acc}
                                  </span>
                                ))}
                              </div>
                            )}
                          </motion.div>
                        )}
                      </GlassCard>
                    </motion.div>
                  )}

                  {/* Career Tab */}
                  {activeTab === 'career' && (
                    <motion.div
                      key="career"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      <PredictionCard title="Career Trajectory" icon={Briefcase} delay={0}>
                        <div className="space-y-4">
                          {preview.content?.career_trajectory && Object.entries(preview.content.career_trajectory).map(([key, value], idx) => (
                            <div key={key} className="p-4 bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)]">
                              <div className="flex items-center gap-2 mb-2">
                                <ChevronRight className="w-4 h-4 text-primary" />
                                <span className="text-sm text-[var(--text-tertiary)] uppercase tracking-wider">
                                  {key.replace(/_/g, ' ')}
                                </span>
                              </div>
                              <p className="text-[var(--text-primary)]">{value}</p>
                            </div>
                          ))}
                        </div>
                      </PredictionCard>

                      {/* Alternative Paths */}
                      {preview.content?.alternative_paths && (
                        <div className="mt-6">
                          <h4 className="text-sm text-[var(--text-tertiary)] mb-3 uppercase tracking-wider">
                            Alternative Career Paths
                          </h4>
                          <div className="grid sm:grid-cols-2 gap-4">
                            {preview.content.alternative_paths.map((path, idx) => (
                              <GlassCard key={idx} className="p-4">
                                <p className="text-[var(--text-primary)]">{typeof path === 'string' ? path : path.title}</p>
                                {path.description && (
                                  <p className="text-sm text-[var(--text-secondary)] mt-2">{path.description}</p>
                                )}
                              </GlassCard>
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* Financial Tab */}
                  {activeTab === 'financial' && (
                    <motion.div
                      key="financial"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      <PredictionCard title="Financial Projections" icon={DollarSign} delay={0}>
                        <div className="space-y-4">
                          {preview.content?.financial_projections && (
                            <>
                              <div className="grid sm:grid-cols-2 gap-4">
                                <div className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/20">
                                  <p className="text-xs text-gray-400 mb-1 uppercase">Starting Income</p>
                                  <p className="font-bold text-lg text-green-400">
                                    {preview.content.financial_projections.starting_income_range}
                                  </p>
                                </div>
                                <div className="p-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl border border-blue-500/20">
                                  <p className="text-xs text-gray-400 mb-1 uppercase">Year 5 Growth</p>
                                  <p className="font-bold text-lg text-blue-400">
                                    {preview.content.financial_projections.year_5_growth}
                                  </p>
                                </div>
                              </div>

                              <div className="p-4 bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)]">
                                <p className="text-sm text-[var(--text-tertiary)] mb-2">Net Worth Trajectory</p>
                                <p className="text-[var(--text-primary)]">
                                  {preview.content.financial_projections.net_worth_trajectory}
                                </p>
                              </div>

                              {preview.content.financial_projections.wealth_building_tips && (
                                <div>
                                  <p className="text-sm text-[var(--text-tertiary)] mb-3">Wealth Building Tips</p>
                                  <div className="space-y-2">
                                    {preview.content.financial_projections.wealth_building_tips.map((tip, idx) => (
                                      <div key={idx} className="flex items-center gap-2 p-3 bg-[var(--bg-card)] rounded-lg">
                                        <TrendingUp className="w-4 h-4 text-green-400" />
                                        <span className="text-sm text-[var(--text-primary)]">{tip}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </PredictionCard>
                    </motion.div>
                  )}

                  {/* Skills Tab */}
                  {activeTab === 'skills' && (
                    <motion.div
                      key="skills"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      <PredictionCard title="Skill Development Roadmap" icon={GraduationCap} delay={0}>
                        <div className="space-y-4">
                          {preview.content?.skill_development && (
                            <>
                              <div className="p-4 bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)]">
                                <p className="text-sm text-[var(--text-tertiary)] mb-2">Current Level</p>
                                <p className="text-[var(--text-primary)]">
                                  {preview.content.skill_development.current_skill_level}
                                </p>
                              </div>

                              {preview.content.skill_development.skills_to_develop && (
                                <div>
                                  <p className="text-sm text-[var(--text-tertiary)] mb-3">Skills to Develop</p>
                                  <div className="flex flex-wrap gap-2">
                                    {preview.content.skill_development.skills_to_develop.map((skill) => (
                                      <span key={skill} className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
                                        {skill}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {preview.content.skill_development.emerging_skills && (
                                <div>
                                  <p className="text-sm text-[var(--text-tertiary)] mb-3">Future-Proof Skills</p>
                                  <div className="flex flex-wrap gap-2">
                                    {preview.content.skill_development.emerging_skills.map((skill) => (
                                      <span key={skill} className="px-3 py-1 bg-accent/20 text-accent rounded-full text-sm">
                                        {skill}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {preview.content.skill_development.certifications_recommended && (
                                <div>
                                  <p className="text-sm text-[var(--text-tertiary)] mb-3">Recommended Certifications</p>
                                  <div className="space-y-2">
                                    {preview.content.skill_development.certifications_recommended.map((cert) => (
                                      <div key={cert} className="flex items-center gap-2 p-3 bg-[var(--bg-card)] rounded-lg">
                                        <Award className="w-4 h-4 text-yellow-400" />
                                        <span className="text-sm text-[var(--text-primary)]">{cert}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </PredictionCard>
                    </motion.div>
                  )}

                  {/* Opportunities Tab */}
                  {activeTab === 'opportunities' && (
                    <motion.div
                      key="opportunities"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      <PredictionCard title="Potential Opportunities" icon={Lightbulb} delay={0}>
                        <div className="space-y-4">
                          {preview.content?.opportunities && (
                            <>
                              {preview.content.opportunities.career_opportunities && (
                                <div>
                                  <p className="text-sm text-[var(--text-tertiary)] mb-3">Career Opportunities</p>
                                  <div className="space-y-2">
                                    {preview.content.opportunities.career_opportunities.map((opp) => (
                                      <div key={opp} className="flex items-center gap-2 p-3 bg-[var(--bg-card)] rounded-lg">
                                        <Target className="w-4 h-4 text-primary" />
                                        <span className="text-sm text-[var(--text-primary)]">{opp}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {preview.content.opportunities.business_opportunities && (
                                <div>
                                  <p className="text-sm text-[var(--text-tertiary)] mb-3">Business Opportunities</p>
                                  <div className="space-y-2">
                                    {preview.content.opportunities.business_opportunities.map((opp) => (
                                      <div key={opp} className="flex items-center gap-2 p-3 bg-[var(--bg-card)] rounded-lg">
                                        <Rocket className="w-4 h-4 text-accent" />
                                        <span className="text-sm text-[var(--text-primary)]">{opp}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {preview.content.opportunities.market_trends && (
                                <div>
                                  <p className="text-sm text-[var(--text-tertiary)] mb-3">Favorable Market Trends</p>
                                  <div className="flex flex-wrap gap-2">
                                    {preview.content.opportunities.market_trends.map((trend) => (
                                      <span key={trend} className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                                        {trend}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className="p-4 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl border border-primary/20">
                                <p className="text-xs text-gray-400 mb-1 uppercase">Timing Recommendation</p>
                                <p className="font-medium text-[var(--text-primary)]">
                                  {preview.content.opportunities.timing_recommendations}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </PredictionCard>
                    </motion.div>
                  )}

                  {/* Personal Growth Tab */}
                  {activeTab === 'growth' && (
                    <motion.div
                      key="growth"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      <PredictionCard title="Personal Growth Suggestions" icon={Heart} delay={0}>
                        <div className="space-y-4">
                          {preview.content?.personal_growth && (
                            <>
                              {preview.content.personal_growth.mindset_shifts && (
                                <div>
                                  <p className="text-sm text-[var(--text-tertiary)] mb-3">Mindset Shifts</p>
                                  <div className="space-y-2">
                                    {preview.content.personal_growth.mindset_shifts.map((shift) => (
                                      <div key={shift} className="flex items-center gap-2 p-3 bg-[var(--bg-card)] rounded-lg">
                                        <Brain className="w-4 h-4 text-purple-400" />
                                        <span className="text-sm text-[var(--text-primary)]">{shift}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {preview.content.personal_growth.habits_to_build && (
                                <div>
                                  <p className="text-sm text-[var(--text-tertiary)] mb-3">Habits to Build</p>
                                  <div className="flex flex-wrap gap-2">
                                    {preview.content.personal_growth.habits_to_build.map((habit) => (
                                      <span key={habit} className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm">
                                        {habit}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className="p-4 bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)]">
                                <p className="text-sm text-[var(--text-tertiary)] mb-2">Work-Life Balance</p>
                                <p className="text-[var(--text-primary)]">
                                  {preview.content.personal_growth.work_life_balance}
                                </p>
                              </div>

                              {preview.content.personal_growth.health_wellness && (
                                <div>
                                  <p className="text-sm text-[var(--text-tertiary)] mb-3">Health & Wellness</p>
                                  <div className="space-y-2">
                                    {preview.content.personal_growth.health_wellness.map((tip) => (
                                      <div key={tip} className="flex items-center gap-2 p-3 bg-[var(--bg-card)] rounded-lg">
                                        <Heart className="w-4 h-4 text-red-400" />
                                        <span className="text-sm text-[var(--text-primary)]">{tip}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </PredictionCard>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Share & Download Options */}
                <div className="mt-6 space-y-3">
                  <div className="flex gap-3">
                    <button
                      onClick={copyShareLink}
                      className="btn-secondary flex-1 flex items-center justify-center gap-2"
                      data-testid="copy-link-btn"
                    >
                      {copiedLink ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                      {copiedLink ? 'Copied!' : 'Copy Link'}
                    </button>
                    <button
                      onClick={shareOnTwitter}
                      className="btn-secondary px-4"
                      title="Share on Twitter"
                    >
                      <Twitter className="w-5 h-5" />
                    </button>
                    <button
                      onClick={shareOnLinkedIn}
                      className="btn-secondary px-4"
                      title="Share on LinkedIn"
                    >
                      <Linkedin className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={downloadAsPNG}
                      disabled={downloadingPNG}
                      className="btn-primary flex-1 flex items-center justify-center gap-2"
                      data-testid="download-png-btn"
                    >
                      {downloadingPNG ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <FileImage className="w-5 h-5" />
                      )}
                      Download PNG
                    </button>
                    <button
                      onClick={downloadAsPDF}
                      disabled={downloadingPDF}
                      className="btn-secondary flex-1 flex items-center justify-center gap-2"
                      data-testid="download-pdf-btn"
                    >
                      {downloadingPDF ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <FileText className="w-5 h-5" />
                      )}
                      Download PDF
                    </button>
                  </div>
                </div>

                {/* Branding Footer */}
                <div className="mt-6 pt-4 border-t border-[var(--border-color)] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img src={LOGO_URL} alt="AIRA ECHO" className="w-6 h-6" crossOrigin="anonymous" />
                    <span className="text-xs text-[var(--text-tertiary)]">Generated by AIRA ECHO</span>
                  </div>
                  <span className="text-xs text-[var(--text-tertiary)]">askaira.ai</span>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="placeholder"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <GlassCard className="p-12 text-center">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-12 h-12 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold mb-2 text-[var(--text-primary)]">
                    See Your Future
                  </h3>
                  <p className="text-[var(--text-secondary)] max-w-md mx-auto">
                    Fill in your profile details and let AIRA's AI brain simulate your future life. 
                    Get predictions for career, finance, skills, opportunities, and personal growth.
                  </p>
                  
                  <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-lg mx-auto">
                    {[
                      { icon: Briefcase, label: 'Career Path' },
                      { icon: DollarSign, label: 'Financial Growth' },
                      { icon: GraduationCap, label: 'Skill Roadmap' },
                      { icon: Lightbulb, label: 'Opportunities' },
                      { icon: Heart, label: 'Personal Growth' },
                      { icon: Calendar, label: 'Life Milestones' }
                    ].map((item, idx) => (
                      <div key={idx} className="p-4 bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)]">
                        <item.icon className="w-6 h-6 text-primary mx-auto mb-2" />
                        <span className="text-xs text-[var(--text-secondary)]">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </GlassCard>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
